import { useCallback, useEffect, useMemo, type ReactElement } from 'react';
import { DataTable } from '@/components/DataTable';
import type { DataFetchParams, TableQueryState } from '@/components/DataTable';
import type { PluginListItem } from '@kirin/api-client/contracts/plugins';
import { SortOrder } from '@kirin/api-client/contracts/helpers';
import { queryKeys } from '@/hooks/api/queryKeys';
import { trackEvent } from '@/lib/analytics';
import { PluginVersionsTable } from '@/components/shared/plugins/PluginVersionsTable';
import { createPluginColumns } from '../DataTableOrgPluginsTab/columns';
import {
  fetchPersonalPlugins,
  fetchPersonalPluginFieldFilterOptions,
} from './api';

/** DataTable-based personal Plugins tab — no Users column / features expansion. */
function DataTablePersonalPluginsContent(): ReactElement {
  const columns = useMemo(
    () =>
      createPluginColumns({
        showUsersColumn: false,
        // Personal scope → wire Name/Marketplace filters, the First Seen date
        // filter, and Source sort against the account filter-options endpoint.
        // No Users column (single-user scope).
        pluginFilters: {
          getQueryKey: (field, params) =>
            queryKeys.plugins.personalFilterOptions(field, params),
          queryFn: (field, params) =>
            fetchPersonalPluginFieldFilterOptions(field, params),
          enableSourceSort: true,
        },
      }),
    []
  );

  // Default sort: name asc (no userCount surface on the personal tab).
  const initialState = useMemo<Partial<TableQueryState>>(
    () => ({ sort: { field: 'name', order: SortOrder.Asc } }),
    []
  );

  useEffect(() => {
    trackEvent('plugins_tab_viewed', { context: 'personal' });
  }, []);

  const getQueryKeysFromParams = useCallback(
    (params: DataFetchParams) => [
      ...queryKeys.plugins.all,
      'personal-data-table',
      {
        page: params.pagination.pageIndex + 1,
        limit: params.pagination.pageSize,
        filters: params.filters,
        sortBy: params.sort?.field,
        sortOrder: params.sort?.order,
        search: params.search,
      },
    ],
    []
  );

  const renderExpandedContent = useCallback(
    (plugin: PluginListItem) => (
      <PluginVersionsTable versions={plugin.versions} />
    ),
    []
  );

  return (
    <DataTable<PluginListItem>
      id="personal-plugins"
      mode="server"
      className="flex flex-col flex-1 min-h-0"
      containerClassName="flex-1"
      columns={columns}
      initialState={initialState}
      errorBoundaryLabel="Something went wrong in the plugins table"
      search={{
        placeholder: 'Search plugins...',
        className: 'w-[260px]',
      }}
      features={{ enableSorting: true, enableRowExpansion: true }}
      rowExpansion={{
        render: renderExpandedContent,
        // Identity is (pluginName, marketplace) — two rows can share a
        // pluginName when the marketplaces differ.
        getRowId: row => `${row.pluginName}@${row.marketplace ?? ''}`,
        expandOnRowClick: true,
        showContainer: false,
        contentClassName: '',
      }}
      queryFn={params => fetchPersonalPlugins(params)}
      getQueryKeysFromParams={getQueryKeysFromParams}
      noDataLabel="No plugins installed."
      queryConfig={{ staleTime: 30000, refetchOnWindowFocus: false }}
    />
  );
}

DataTablePersonalPluginsContent.displayName = 'DataTablePersonalPluginsContent';

export default function DataTablePluginsTab(): ReactElement {
  return <DataTablePersonalPluginsContent />;
}
